import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Briefcase, ListChecks, ArrowRight, Heart, AlertCircle, Flame } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../lib/branding";

function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 5) return "Hello";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const branding = useBranding();
  const [kpi, setKpi] = useState({
    leads_need_love: 0,
    overdue_moves: 0,
    deals_need_love: 0,
    todays_moves: 0,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard");
        if (!cancelled) setKpi(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const firstName = user?.first_name || (user?.name || "").split(" ")[0] || "there";
  const greeting = timeOfDayGreeting();

  // Replace {greeting} and {first_name} tokens so superadmins can customise
  // the copy while the per-user context still renders correctly.
  const interpolate = (tpl) =>
    (tpl || "")
      .replaceAll("{greeting}", greeting)
      .replaceAll("{first_name}", firstName);
  const line1 = interpolate(branding.dashboard_welcome_line_1);
  const line2 = interpolate(branding.dashboard_welcome_line_2);
  const line3 = interpolate(branding.dashboard_welcome_line_3);

  return (
    <div className="dashboard-page" data-testid="dashboard-page">
      {/* ─── Hero greeting ─── */}
      <div className="dash-hero">
        <h1 className="dash-hero-headline" data-testid="dashboard-headline">
          {line1}
          {(line2 || line3) && <br />}
          {line2}
          {line2 && line3 && " "}
          {line3 && <strong>{line3}</strong>}
        </h1>
      </div>

      {/* ─── Quick moves ─── */}
      <div className="quick-moves">
        <button
          type="button"
          className="quick-move-card"
          onClick={() => navigate("/leads?new=1")}
          data-testid="quick-add-lead"
        >
          <div className="quick-move-icon" style={{ background: "rgba(91, 79, 232, 0.10)", color: "var(--pg-purple)" }}>
            <UserPlus strokeWidth={1.8} />
          </div>
          <div className="quick-move-title">Add Lead</div>
          <div className="quick-move-desc">Do you have a new lead we can add?</div>
          <div className="quick-move-cta">Add a lead <ArrowRight strokeWidth={2} /></div>
        </button>

        <button
          type="button"
          className="quick-move-card"
          onClick={() => navigate("/deals")}
          data-testid="quick-view-deals"
        >
          <div className="quick-move-icon" style={{ background: "rgba(143, 191, 0, 0.18)", color: "#5A7A00" }}>
            <Briefcase strokeWidth={1.8} />
          </div>
          <div className="quick-move-title">View Deals</div>
          <div className="quick-move-desc">Let&apos;s get these deals moving in the right direction.</div>
          <div className="quick-move-cta">Open Kanban <ArrowRight strokeWidth={2} /></div>
        </button>

        <button
          type="button"
          className="quick-move-card"
          onClick={() => navigate("/moves")}
          data-testid="quick-todays-moves"
        >
          <div className="quick-move-icon" style={{ background: "rgba(232, 91, 26, 0.10)", color: "var(--pg-orange)" }}>
            <ListChecks strokeWidth={1.8} />
          </div>
          <div className="quick-move-title">Today&apos;s Moves</div>
          <div className="quick-move-desc">Want to get straight into things? Let&apos;s see your move list.</div>
          <div className="quick-move-cta">
            {kpi.todays_moves > 0 ? `${kpi.todays_moves} due today` : "View list"} <ArrowRight strokeWidth={2} />
          </div>
        </button>
      </div>

      {/* ─── KPI cards ─── */}
      <div className="dash-kpi-row">
        <div
          className="dash-kpi-card"
          onClick={() => navigate("/leads")}
          data-testid="kpi-leads-need-love"
          role="button"
          tabIndex={0}
        >
          <div className="dash-kpi-icon" style={{ background: "rgba(91, 79, 232, 0.10)", color: "var(--pg-purple)" }}>
            <Heart strokeWidth={1.8} />
          </div>
          <div className="dash-kpi-content">
            <div className="dash-kpi-label">Leads that need love</div>
            <div className="dash-kpi-value">{kpi.leads_need_love}</div>
            <div className="dash-kpi-sub">Untouched or stale &gt; 7d</div>
          </div>
        </div>

        <div
          className="dash-kpi-card"
          onClick={() => navigate("/moves")}
          data-testid="kpi-overdue-moves"
          role="button"
          tabIndex={0}
        >
          <div className="dash-kpi-icon" style={{ background: "var(--pg-red-soft)", color: "var(--pg-red)" }}>
            <AlertCircle strokeWidth={1.8} />
          </div>
          <div className="dash-kpi-content">
            <div className="dash-kpi-label">Overdue Moves</div>
            <div className="dash-kpi-value">{kpi.overdue_moves}</div>
            <div className="dash-kpi-sub">Assigned to you, past due</div>
          </div>
        </div>

        <div
          className="dash-kpi-card"
          onClick={() => navigate("/deals")}
          data-testid="kpi-deals-need-love"
          role="button"
          tabIndex={0}
        >
          <div className="dash-kpi-icon" style={{ background: "rgba(232, 91, 26, 0.10)", color: "var(--pg-orange)" }}>
            <Flame strokeWidth={1.8} />
          </div>
          <div className="dash-kpi-content">
            <div className="dash-kpi-label">Deals that need love</div>
            <div className="dash-kpi-value">{kpi.deals_need_love}</div>
            <div className="dash-kpi-sub">Stuck in stage &gt; 14d</div>
          </div>
        </div>
      </div>
    </div>
  );
}
